import React, { useState, useEffect, useCallback } from 'react';
import { getAllUsers, updateUserRole, updateUserActiveStatus } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';

// MUI Components
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';

// MUI Icons
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';

// User role chip styles
const getRoleChipStyle = (role: string) => {
  switch (role) {
    case 'ADMIN':
      return { bg: '#F0EDFF', color: '#7065F0' };
    case 'AGENT':
      return { bg: '#E1FCEF', color: '#10B981' };
    case 'OWNER':
      return { bg: '#FFF4E5', color: '#FF8A00' };
    case 'USER':
      return { bg: '#F0F9FF', color: '#0EA5E9' };
    default:
      return { bg: '#F3F4F6', color: '#6B7280' };
  }
};

const UsersManagement: React.FC = () => {
  const { admin } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newRole, setNewRole] = useState<string>('');
  const [statusChangeLoading, setStatusChangeLoading] = useState<{[key: string]: boolean}>({});

  // Fetch users
  const fetchUsers = async (search = searchTerm) => {
    try {
      setLoading(true);
      const response = await getAllUsers({
        search,
        role: filterRole,
        page: page + 1,
        limit: rowsPerPage,
      });
      
      setUsers(response.users);
      setTotalUsers(response.pagination.totalUsers);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Create a debounced version of search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      fetchUsers(searchValue);
    }, 500),
    [filterRole, page, rowsPerPage]
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Initial fetch
  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, filterRole]);

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open edit dialog
  const handleOpenEditDialog = (user: any) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setEditDialogOpen(true);
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setNewRole('');
  };

  // Update user role
  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;

    try {
      await updateUserRole(selectedUser._id, newRole);
      toast.success('User role updated successfully');
      
      // Update local state to reflect the change
      setUsers(users.map(u => 
        u._id === selectedUser._id 
          ? { ...u, role: newRole } 
          : u
      ));
      
      handleCloseEditDialog();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user role');
    }
  };

  // Handle active status toggle
  const handleActiveStatusChange = async (userId: string, newStatus: boolean) => {
    try {
      setStatusChangeLoading(prev => ({ ...prev, [userId]: true }));
      await updateUserActiveStatus(userId, newStatus);
      
      // Update local state to reflect the change
      setUsers(users.map(u => 
        u._id === userId 
          ? { ...u, active: newStatus } 
          : u
      ));
      
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user status');
    } finally {
      setStatusChangeLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#0F0F2D', mb: 4 }}>
        Users Management
      </Typography>

      {/* Filters and Search */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3, 
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2 
        }}
      >
        <TextField
          placeholder="Search users..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="role-filter-label">Role</InputLabel>
          <Select
            labelId="role-filter-label"
            value={filterRole}
            label="Role"
            onChange={(e) => setFilterRole(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <FilterListIcon fontSize="small" />
              </InputAdornment>
            }
          >
            <MenuItem value="">All Roles</MenuItem>
            <MenuItem value="OWNER">Owner</MenuItem>
            <MenuItem value="USER">User</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Users Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#F7F7FD' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Active Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={40} sx={{ color: '#7065F0' }} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const roleStyle = getRoleChipStyle(user.role);
                const isLoading = statusChangeLoading[user._id] || false;
                
                return (
                  <TableRow key={user._id} hover>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role} 
                        size="small"
                        sx={{ 
                          backgroundColor: roleStyle.bg,
                          color: roleStyle.color,
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={user.active !== false} // Default to true if not explicitly false
                            onChange={(e) => handleActiveStatusChange(user._id, e.target.checked)}
                            disabled={isLoading}
                            color="primary"
                          />
                        }
                        label={
                          isLoading ? 
                            <CircularProgress size={16} sx={{ ml: 1 }} /> : 
                            (user.active !== false ? "Active" : "Inactive")
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Edit User Role Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Edit User Role
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Update the role for {selectedUser?.name}
          </DialogContentText>
          
          <FormControl fullWidth>
            <InputLabel id="edit-role-label">Role</InputLabel>
            <Select
              labelId="edit-role-label"
              value={newRole}
              label="Role"
              onChange={(e) => setNewRole(e.target.value)}
            >
              <MenuItem value="OWNER">Owner</MenuItem>
              <MenuItem value="USER">User</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            onClick={handleUpdateRole} 
            variant="contained"
            sx={{
              backgroundColor: '#7065F0',
              '&:hover': {
                backgroundColor: '#5A52C4',
              },
            }}
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersManagement; 